import { CohortInputMap, CohortInputRule, DEFAULT_COHORT_RULE, normalizeCohortRule } from './cohort-forecast';

export type MixCellKind = 'actual' | 'override' | 'rule' | 'fallback';

export interface MixForecastCategory {
  name: string;
  base: number | null | undefined;
  history?: Record<string, number> | null;
}

export interface MixForecastInput {
  months: number[];
  cut: number;
  cats: MixForecastCategory[];
  override?: (ci: number, t: number) => number | null | undefined;
  ruleFor: (name: string) => CohortInputRule;
  cellRuleFor?: (name: string, period: number) => CohortInputRule | null | undefined;
  weights?: Array<number | null | undefined> | null;
  allowNegative?: boolean;
  zeroMissing?: boolean;
}

export interface MixForecastResult {
  values: Array<Array<number | null>>;
  base: Array<Array<number | null>>;
  kinds: Array<Array<MixCellKind | null>>;
}

type Grid = Array<Array<number | null>>;

export const MANUAL_CATEGORY_NOTE = 'Manual category · saved in the app, not Snowflake. Every month is editable and accepts + or − values to adjust actuals.';
export const MANUAL_MIX_ACTUAL_NOTE = 'Manual adjustment for this actual month. Type a + or − value; it adjusts the actual mix and COGS in the totals.';

const SEASON = 12;
export const TREND_MIN = 0.5;
export const TREND_MAX = 2;

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

export function mixInputKey(ch: string, name?: string | null, period?: number | null): string {
  if (!name) return ch;
  return period ? ch + '|' + name + '|' + period : ch + '|' + name;
}

const isPeriod = (s: string) => /^\d{6}$/.test(s) && +s.slice(4) >= 1 && +s.slice(4) <= 12;

export function sanitizeMixInput(raw: unknown): CohortInputMap {
  const out: CohortInputMap = {};
  if (!raw || typeof raw !== 'object') return out;
  Object.keys(raw as Record<string, unknown>).forEach(key => {
    const parts = key.split('|');
    if (parts.length < 1 || parts.length > 3 || parts.some(p => !p)) return;
    if (parts.length === 3 && !isPeriod(parts[2])) return;
    const rule = normalizeCohortRule((raw as Record<string, unknown>)[key]);
    if (rule) out[key] = rule;
  });
  return out;
}

export function resolveMixRule(map: CohortInputMap | null | undefined, ch: string, name?: string | null, period?: number | null): CohortInputRule {
  const m = map || {};
  if (name && period) {
    const cell = m[mixInputKey(ch, name, period)];
    if (cell) return cell;
  }
  if (name) {
    const own = m[mixInputKey(ch, name)];
    if (own) return own;
  }
  return m[mixInputKey(ch)] || DEFAULT_COHORT_RULE;
}

export function describeMixRule(rule: CohortInputRule): string {
  switch (rule.method) {
    case 'prev':
      if (rule.n === SEASON) return 'Same month last year (−12)';
      return rule.n === 1 ? 'Previous month (−1)' : 'Previous pattern −' + rule.n + ' months';
    case 'avg': return 'Average of last ' + rule.n + ' months';
    case 'wavg': return 'Weighted average of last ' + rule.n + ' months';
    case 'chain': return 'Continue own curve · last ' + rule.n + ' months';
    case 'seasonal': return 'Seasonal with trend · ' + rule.n + ' months';
    default: return 'Fixed ' + (isNum(rule.value) ? Math.round(rule.value * 1000) / 10 : 0) + '%';
  }
}

export function mixRuleTag(rule: CohortInputRule): string {
  switch (rule.method) {
    case 'prev': return rule.n === SEASON ? 'LY' : 'PREV ' + rule.n;
    case 'avg': return 'AVG ' + rule.n;
    case 'wavg': return 'WAVG ' + rule.n;
    case 'chain': return 'CHAIN ' + rule.n;
    case 'seasonal': return 'SEAS ' + rule.n;
    default: return 'FIX ' + (isNum(rule.value) ? Math.round(rule.value * 1000) / 10 : 0) + '%';
  }
}

export function forecastMix(input: MixForecastInput): MixForecastResult {
  const { months, cut, cats, override, ruleFor, cellRuleFor, weights } = input;
  const n = months.length;
  const series: Array<Array<number | null>> = cats.map(() => new Array(n).fill(null));
  const kinds: Array<Array<MixCellKind | null>> = cats.map(() => new Array(n).fill(null));
  const covered = new Set<string>();
  if (input.zeroMissing) {
    cats.forEach(c => {
      const h = c.history;
      if (h) Object.keys(h).forEach(k => { if (isNum(h[k])) covered.add(k); });
    });
  }

  cats.forEach((c, ci) => {
    for (let t = 0; t <= Math.min(cut, n - 1); t++) {
      const key = String(months[t]);
      const h = c.history ? c.history[key] : undefined;
      const v = isNum(h) ? h : covered.has(key) ? 0 : c.base;
      if (isNum(v)) { series[ci][t] = v; kinds[ci][t] = 'actual'; }
    }
  });

  const read = (src: Grid, ci: number, t: number): number | null => (t >= 0 && isNum(src[ci][t]) ? src[ci][t] : null);
  const get = (ci: number, t: number) => read(series, ci, t);
  const weightAt = (t: number) => (weights && isNum(weights[t]) && (weights[t] as number) > 0 ? (weights[t] as number) : 1);
  const factorCache: Record<string, number> = {};
  const chainFactor = (ci: number, count: number): number => {
    const key = ci + '|' + count;
    if (factorCache[key] !== undefined) return factorCache[key];
    let num = 0, den = 0, used = 0;
    for (let x = Math.min(cut, n - 1); x >= 1 && used < count; x--) {
      const hi = get(ci, x), lo = get(ci, x - 1);
      if (hi === null || lo === null) continue;
      num += hi;
      den += lo;
      used++;
    }
    factorCache[key] = den > 0 ? num / den : 1;
    return factorCache[key];
  };

  const evaluate = (src: Grid, ci: number, t: number, rule: CohortInputRule): number | null => {
    const count = rule.n || 1;
    switch (rule.method) {
      case 'prev':
        return read(src, ci, t - count);
      case 'avg': {
        let sum = 0, used = 0;
        for (let k = 1; k <= count; k++) {
          const v = read(src, ci, t - k);
          if (v === null) continue;
          sum += v;
          used++;
        }
        return used ? sum / used : null;
      }
      case 'wavg': {
        let sum = 0, w = 0;
        for (let k = 1; k <= count; k++) {
          const v = read(src, ci, t - k);
          if (v === null) continue;
          sum += weightAt(t - k) * v;
          w += weightAt(t - k);
        }
        return w ? sum / w : null;
      }
      case 'chain': {
        const own = read(src, ci, t - 1);
        return own === null ? null : own * chainFactor(ci, count);
      }
      case 'seasonal': {
        const base = read(src, ci, t - SEASON);
        if (base === null) return null;
        let num = 0, den = 0;
        for (let k = 1; k <= count; k++) {
          const recent = read(src, ci, t - k), prior = read(src, ci, t - SEASON - k);
          if (recent === null || prior === null) continue;
          num += recent;
          den += prior;
        }
        return den > 0 ? base * Math.max(TREND_MIN, Math.min(TREND_MAX, num / den)) : base;
      }
      default:
        return isNum(rule.value) ? rule.value : null;
    }
  };

  const lastBefore = (src: Grid, ci: number, t: number): number | null => {
    for (let x = t - 1; x >= 0; x--) {
      const v = read(src, ci, x);
      if (v !== null) return v;
    }
    const b = cats[ci].base;
    return isNum(b) ? b : null;
  };

  const floor = (v: number) => (input.allowNegative ? v : Math.max(0, v));

  const solve = (src: Grid, ci: number, t: number, rule: CohortInputRule): [number | null, MixCellKind] => {
    const v = evaluate(src, ci, t, rule);
    if (v !== null) return [floor(v), 'rule'];
    const f = lastBefore(src, ci, t);
    return [f === null ? null : floor(f), 'fallback'];
  };

  cats.forEach((c, ci) => {
    const rule = ruleFor(c.name);
    for (let t = Math.max(0, cut + 1); t < n; t++) {
      const [v, kind] = solve(series, ci, t, rule);
      series[ci][t] = v;
      if (v !== null) kinds[ci][t] = kind;
    }
  });

  const values = series.map(row => row.slice());
  cats.forEach((c, ci) => {
    for (let t = Math.max(0, cut + 1); t < n; t++) {
      const ov = override ? override(ci, t) : undefined;
      if (isNum(ov)) { values[ci][t] = ov; kinds[ci][t] = 'override'; continue; }
      const cellRule = cellRuleFor ? cellRuleFor(c.name, months[t]) : null;
      if (!cellRule) continue;
      const [v, kind] = solve(values, ci, t, cellRule);
      values[ci][t] = v;
      kinds[ci][t] = v === null ? null : kind;
    }
  });

  return { values, base: series, kinds };
}

export function mixRuleSources(rule: CohortInputRule, t: number): number[] {
  const count = rule.n || 1;
  const back = (k: number) => Array.from({ length: k }, (_, i) => t - i - 1);
  switch (rule.method) {
    case 'prev': return [t - count];
    case 'avg':
    case 'wavg': return back(count);
    case 'chain': return [t - 1];
    case 'seasonal': return [t - SEASON].concat(back(count));
    default: return [];
  }
}
